/* eslint-disable no-nested-ternary */
import type { MenuProps } from '@mantine/core';
import { UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { ListBox } from '@repo/foundations';
import { isNil } from 'lodash-es';
import { memo, useCallback, useMemo } from 'react';
import type { FilterOperatorConfig } from './constants';
import { IS_NOT_SET_FILTER_ITEM, IS_SET_FILTER_ITEM } from './constants';
import { FilterDropdown } from './FilterDropdown/FilterDropdown';
import type { FilterNestedTargetMenuProps } from './FilterTarget/FilterNestedTargetMenu';
import { FilterTargetDetailed } from './FilterTarget/FilterTargetDetailed';
import { FilterTargetDetailedReadOnly } from './FilterTarget/FilterTargetDetailedReadOnly';
import { FilterTargetSimple } from './FilterTarget/FilterTargetSimple';
import type { OperatorMenuProps } from './FilterTarget/OperatorMenu';
import type { FilterOption, FilterValue } from './types';
import { FilterDropdownType, FilterOperator } from './types';
import { useFilter } from './useFilter';
import { useFilterLabel } from './useFilterLabel';
import { getValueAsArray } from './utils';

export interface FilterProps extends Omit<MenuProps, 'opened' | 'onChange'> {
	value: FilterValue;
	showDetailedLabel?: boolean;
	onClear?: () => void;
	onChange: (value: Partial<FilterValue>) => void;
	initialOpened?: boolean;
	operatorConfig?: FilterOperatorConfig;
	filterOption: FilterOption;
	nestedFilterOption?: FilterOption;
	children?: React.ReactNode;
	readOnly?: boolean;
}

function FilterInternal({
	value,
	showDetailedLabel = false,
	onClear,
	onChange,
	initialOpened = false,
	operatorConfig,
	filterOption,
	children,
	readOnly,
	nestedFilterOption,
	...menuProps
}: FilterProps) {
	const [opened, { close, toggle, open }] = useDisclosure(initialOpened);
	const [
		nestedOpened,
		{ close: closeNested, toggle: toggleNested, open: openNested },
	] = useDisclosure(false);

	const isNestedList = useMemo(
		() =>
			filterOption.filterDropdownConfig.dropdownType ===
			FilterDropdownType.NestedList,
		[filterOption.filterDropdownConfig.dropdownType]
	);

	const handleOnOpenChange = useCallback(
		(newOpen: boolean) => {
			if (newOpen) {
				open();
			} else {
				close();
			}
		},
		[close, open]
	);

	const valueLabel = useFilterLabel({ filterOption, value });

	const { handleOnChange } = useFilter({
		value,
		onChange,
		close,
		isNestedList,
		openNested,
	});

	const operatorMenuProps = useMemo(() => {
		let operators = operatorConfig?.getOperators(value) ?? [];

		// Filter out string operators if there's no actual operand value
		if (
			(!value.value ||
				(typeof value.value === 'string' && value.value.trim() === '')) &&
			filterOption.filterDropdownConfig.dropdownType ===
				FilterDropdownType.String
		) {
			const stringOperators = [
				FilterOperator.Contains,
				FilterOperator.DoesNotContain,
				FilterOperator.StartsWith,
				FilterOperator.EndsWith,
				FilterOperator.Is,
				FilterOperator.IsNot,
			];
			operators = operators.filter((op) => !stringOperators.includes(op));
		}

		if (filterOption.filterDropdownConfig.hasIsNotSetOption) {
			// if option supports isNotSet, we add it to the list of operators
			operators.push(FilterOperator.isNotSet);
		}
		if (filterOption.filterDropdownConfig.hasIsSetOption) {
			// if option supports isSet, we add it to the list of operators
			operators.push(FilterOperator.isSet);
		}

		let selectedOperator = value.operator;
		// if option supports isNotSet, isNotSet is applied, and value is empty, we should set isNotSet as the operator for display purposes
		if (
			filterOption.filterDropdownConfig.hasIsNotSetOption &&
			value.isNotSetApplied &&
			(!filterOption.filterDropdownConfig.hasIsSetOption ||
				!value.isSetApplied) &&
			isNil(value.value)
		) {
			selectedOperator = FilterOperator.isNotSet;
		}

		// if option supports isSet, isSet is applied, and value is empty, we should set isSet as the operator for display purposes
		if (
			filterOption.filterDropdownConfig.hasIsSetOption &&
			value.isSetApplied &&
			(!filterOption.filterDropdownConfig.hasIsNotSetOption ||
				!value.isNotSetApplied) &&
			isNil(value.value)
		) {
			selectedOperator = FilterOperator.isSet;
		}

		return {
			operators: operators.map((item) => ({
				value: item,
				label: operatorConfig?.getLabel(item, value.value) ?? item.toString(),
			})),
			selected: selectedOperator,
			onChange: (operator) => {
				const valueAsArray = getValueAsArray(value);
				const newValue: Partial<FilterValue> = {
					operator,
					isNotSetApplied:
						operator === FilterOperator.isNotSet ||
						valueAsArray.includes(IS_NOT_SET_FILTER_ITEM.value),
					isSetApplied:
						operator === FilterOperator.isSet ||
						valueAsArray.includes(IS_SET_FILTER_ITEM.value),
				};

				// Unset value if operator changes and dropdown type is nested list
				if (isNestedList) {
					newValue.value = null;
				}

				handleOnChange(newValue, true);

				// if we are switching to other operator than isNotSet and value is empty, we should open dropdown for value selection
				// Need to toggle if operator changes in nested list
				if (
					(!newValue.isNotSetApplied &&
						!newValue.isSetApplied &&
						isNil(value.value) &&
						!opened) ||
					isNestedList
				) {
					toggle();
				}
			},
			label:
				operatorConfig?.getLabel(selectedOperator, value.value) ??
				selectedOperator.toString(),
		} as OperatorMenuProps;
	}, [
		filterOption,
		handleOnChange,
		isNestedList,
		opened,
		operatorConfig,
		toggle,
		value,
	]);

	const nestedMenuProps: FilterNestedTargetMenuProps | null = useMemo(() => {
		if (
			!nestedFilterOption ||
			nestedFilterOption.filterDropdownConfig.dropdownType !==
				FilterDropdownType.List
		) {
			return null;
		}

		return {
			value,
			onChange: handleOnChange,
			onToggle: toggleNested,
			opened: nestedOpened,
			config: nestedFilterOption?.filterDropdownConfig,
			closeNested,
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		value,
		handleOnChange,
		toggleNested,
		nestedFilterOption?.filterDropdownConfig,
		nestedFilterOption,
		nestedOpened,
		closeNested,
	]);

	const hasValue =
		!isNil(value.value) ||
		value.isNotSetApplied ||
		value.operator === FilterOperator.isNotSet ||
		value.isSetApplied ||
		value.operator === FilterOperator.isSet;
	const canShowDetailedLabel =
		showDetailedLabel && !!operatorConfig && hasValue;

	const memoizedButton = useMemo(
		() =>
			children ? (
				<UnstyledButton onClick={toggle}>{children}</UnstyledButton>
			) : null,
		[children, toggle]
	);

	return (
		<ListBox
			closeOnClickOutside
			{...menuProps}
			opened={opened}
			onOpenChange={handleOnOpenChange}
			autoAdjustPosition={false}
		>
			<ListBox.Target>
				{memoizedButton ? (
					memoizedButton
				) : canShowDetailedLabel ? (
					readOnly ? (
						<FilterTargetDetailedReadOnly
							label={filterOption.label}
							valueLabel={valueLabel}
							operatorMenuProps={operatorMenuProps}
						/>
					) : (
						<FilterTargetDetailed
							label={filterOption.label}
							valueLabel={valueLabel}
							onClear={onClear}
							isMenuOpen={opened}
							operatorMenuProps={operatorMenuProps}
							onToggle={toggle}
							isNestedList={isNestedList}
							nestedMenuProps={nestedMenuProps}
						/>
					)
				) : (
					<FilterTargetSimple
						label={valueLabel ?? filterOption.label}
						onClear={onClear}
						isMenuOpen={opened}
						onToggle={toggle}
						hasValue={hasValue}
					/>
				)}
			</ListBox.Target>
			{opened && (
				<FilterDropdown
					filterOption={filterOption}
					onChange={handleOnChange}
					value={value}
					operator={operatorMenuProps.selected}
				/>
			)}
		</ListBox>
	);
}

export const Filter = memo(FilterInternal);
